import { Component, effect, inject, input, linkedSignal, model, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideCircleAlert, lucideDot } from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { NgClass } from '@angular/common';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { Cartographie } from '../../cartographie/models/cartographie.model';
import { BlocCompétences } from '../../croisement-référentiel/models/bloc-compétences';
import { BlocÉtat } from '../../croisement-référentiel/models/bloc-état';
import { Compétence } from '../../croisement-référentiel/models/compétences';
import { Aptitude } from '../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { AptitudeStore } from '../../référentiel-métier/référentiel-métier-processus/processus-aptitude/store/aptitude.store';

import { InputSearchFilterComponent } from '../../shared/components/input-search-filter/input-search-filter.component';
import { ToastrErrorComponent } from '../../shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { BlocCompétencesStore } from '../store/bloc-compétences.store';
import { CompétenceStore } from '../store/compétences.store';

@Component({
  selector: 'app-aptitudes',
  imports: [
    HlmButtonImports,
    BrnSelectImports,
    HlmSelectImports,
    HlmIconImports,
    HlmTooltipImports,
    HlmCheckboxImports,
    RouterModule,
    InputSearchFilterComponent,
    NgClass,
  ],
  templateUrl: './aptitudes.component.html',
  providers: [
    provideIcons({
      lucideDot,
      lucideCheck,
      lucideCircleAlert,
    }),
  ],
})
export class AptitudesComponent implements OnDestroy {
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #competenceStore = inject(CompétenceStore);
  readonly #blocCompetenceStore = inject(BlocCompétencesStore);

  readonly aptitudes = linkedSignal(() => this.#aptitudeStore.entities());
  readonly referentiel = input.required<Référentiel>();
  readonly cartographie = input.required<Cartographie>();

  readonly bloc = input.required<BlocCompétences>();
  readonly idCompetence = input<string>();
  readonly competenceSelected = signal<Compétence | undefined>(undefined);
  isErrorVisible = signal<boolean>(false);
  readonly filtre = model<string>('');

  constructor() {
    effect(() => {
      const idCartographie = this.cartographie().id;
      if (idCartographie) this.#referentielStore.chargerParCartographie(idCartographie);

      this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { idCompetence: this.bloc().competences[0].id },
        queryParamsHandling: 'replace',
      });
    });

    effect(() => {
      this.competenceSelected.set(this.bloc().competences.find((c) => c.id == this.idCompetence()));
    });
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  filtrer($event: { filtre: string; idReferentiel?: string }) {
    this.filtre.set($event.filtre);

    if (this.filtre()) {
      this.#aptitudeStore.charger({
        idCartographie: this.cartographie().id,
        idRéférentiel: $event.idReferentiel,
        nom: this.filtre(),
      });
    }
    this.getAptitudesSorted($event.idReferentiel);
  }

  getAptitudesSorted(idReferentiel?: string) {
    this.#aptitudeStore.charger({ idCartographie: this.cartographie().id, idRéférentiel: idReferentiel });
  }

  changed(aptitude: Aptitude) {
    const competence = this.competenceSelected();

    if (!competence) return;

    let aptitudesCompetences = competence.aptitudes ?? [];

    const isAptitudeFound = aptitudesCompetences.some((a) => a.id == aptitude.id);
    if (!isAptitudeFound) {
      competence.aptitudes.push(aptitude);
    } else {
      const index = aptitudesCompetences.findIndex((a) => a.id == aptitude.id);
      competence.aptitudes.splice(index, 1);
    }

    this.#competenceStore.modifier({
      competence: competence,
      idReferentiel: this.referentiel().id,
    });
  }

  isChecked(aptitude: Aptitude) {
    const competence = this.competenceSelected();
    return competence ? competence.aptitudes.some((a) => a.id == aptitude.id) : false;
  }

  hasError(competence: Compétence) {
    return competence.aptitudes.length < 1 && this.isErrorVisible();
  }

  submit() {
    const bloc = this.bloc();
    const competencesSansAptitudes = this.bloc().competences.filter((c) => c.aptitudes.length < 1).length;
    const hasError = competencesSansAptitudes > 0;

    const toastrId = uuidv7();

    if (hasError) {
      this.isErrorVisible.set(true);
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          titre:
            competencesSansAptitudes > 1
              ? "Plusieurs compétences n'ont pas été associées."
              : "Une compétence n'a pas été associée",
          message:
            'Il est nécessaire d’associer au minimum une aptitude à une compétence pour que la compétence soit considérée comme valide.',
          id: toastrId,
        },
        duration: Number.POSITIVE_INFINITY,
        id: toastrId,
      });

      return;
    }

    bloc.etat = BlocÉtat.FAIT;
    this.#blocCompetenceStore.modifier({
      bloc: bloc,
      idReferentiel: this.referentiel().id,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            titre: 'Le croisement du bloc de compétences a bien été effectué.',
            message: 'Les informations que vous avez renseignées ont bien été enregistrées.',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#router.navigate(['../..'], { relativeTo: this.#route });
      },
    });
  }
}
